"use client";

import { useState, useRef } from "react";

type Status = "idle" | "loading" | "ok" | "error";
type FieldErrors = { name?: string; email?: string; message?: string };

const SERVICES = [
  { value: "ciberseguridad",  label: "Ciberseguridad" },
  { value: "redes",           label: "Redes e infraestructura" },
  { value: "videovigilancia", label: "Videovigilancia CCTV" },
  { value: "software",        label: "Software a la medida" },
  { value: "cloud",           label: "Cloud & servidores" },
  { value: "soporte",         label: "Soporte técnico" },
  { value: "otro",            label: "Otro" },
];

const inputClass =
  "w-full bg-white/[0.03] border border-white/10 hover:border-white/20 focus:border-cyan-500/70 focus:bg-white/[0.05] text-white placeholder:text-gray-600 rounded-lg px-4 py-3 text-sm outline-none transition-all duration-200";

const errorInputClass =
  "w-full bg-white/[0.03] border border-red-500/50 focus:border-red-500/70 text-white placeholder:text-gray-600 rounded-lg px-4 py-3 text-sm outline-none transition-all duration-200";

function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="block text-[10px] font-semibold tracking-[0.18em] text-gray-500 uppercase mb-2">
      {children}
      {required && <span className="text-cyan-500 ml-0.5">*</span>}
    </label>
  );
}

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <p className="mt-1.5 text-xs text-red-400">{msg}</p>;
}

function SuccessState({ isEs }: { isEs: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {/* Checkmark circle */}
      <div className="relative mb-6">
        <div className="w-16 h-16 rounded-full border border-cyan-500/40 flex items-center justify-center">
          <svg className="w-7 h-7 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        {/* Ping */}
        <div className="absolute inset-0 rounded-full border border-cyan-500/20 animate-ping" />
      </div>

      <h3 className="text-white font-semibold text-lg mb-2">
        {isEs ? "¡Mensaje recibido!" : "Message received!"}
      </h3>
      <p className="text-gray-500 text-sm max-w-xs leading-relaxed">
        {isEs
          ? "Te contactaremos en menos de 24 horas. También puedes escribirnos por WhatsApp."
          : "We'll contact you in less than 24 hours. You can also reach us on WhatsApp."}
      </p>
      <a
        href="https://wa.me/50762219395"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white border border-white/10 hover:border-white/25 px-4 py-2 rounded-lg transition-colors"
      >
        <WaIcon />
        +507 6221-9395
      </a>
    </div>
  );
}

function WaIcon() {
  return (
    <svg className="w-4 h-4 fill-current text-green-400" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 0C5.373 0 0 5.373 0 12c0 2.127.556 4.123 1.526 5.855L.057 23.882l6.205-1.628A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.017-1.374l-.36-.214-3.732.979.995-3.633-.234-.374A9.818 9.818 0 1112 21.818z" />
    </svg>
  );
}

export default function ContactForm({ isEs = true }: { isEs?: boolean }) {
  const [status, setStatus]     = useState<Status>("idle");
  const [errors, setErrors]     = useState<FieldErrors>({});
  const [msgLen, setMsgLen]     = useState(0);
  const formRef                 = useRef<HTMLFormElement>(null);

  function validate(data: Record<string, string>): FieldErrors {
    const e: FieldErrors = {};
    if (!data.name || data.name.trim().length < 2)
      e.name = isEs ? "Nombre requerido (mín. 2 caracteres)." : "Name required (min. 2 chars).";
    if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      e.email = isEs ? "Email inválido." : "Invalid email.";
    if (!data.message || data.message.trim().length < 10)
      e.message = isEs ? "Mensaje demasiado corto (mín. 10 caracteres)." : "Message too short (min. 10 chars).";
    return e;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const raw  = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    const errs = validate(raw);

    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setStatus("loading");

    try {
      const res = await fetch("/api/contact", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(raw),
      });
      setStatus(res.ok ? "ok" : "error");
    } catch {
      setStatus("error");
    }
  }

  if (status === "ok") return <SuccessState isEs={isEs} />;

  return (
    <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-5">

      {/* Nombre + Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label required>{isEs ? "Nombre completo" : "Full name"}</Label>
          <input
            name="name"
            autoComplete="name"
            placeholder={isEs ? "Juan Pérez" : "John Doe"}
            className={errors.name ? errorInputClass : inputClass}
            onChange={() => errors.name && setErrors(p => ({ ...p, name: undefined }))}
          />
          <FieldError msg={errors.name} />
        </div>
        <div>
          <Label required>{isEs ? "Correo electrónico" : "Email address"}</Label>
          <input
            name="email"
            type="email"
            autoComplete="email"
            placeholder="juan@empresa.com"
            className={errors.email ? errorInputClass : inputClass}
            onChange={() => errors.email && setErrors(p => ({ ...p, email: undefined }))}
          />
          <FieldError msg={errors.email} />
        </div>
      </div>

      {/* Empresa + Servicio */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label>{isEs ? "Empresa" : "Company"}</Label>
          <input
            name="company"
            autoComplete="organization"
            placeholder={isEs ? "Mi Empresa S.A." : "My Company Inc."}
            className={inputClass}
          />
        </div>
        <div>
          <Label>{isEs ? "Servicio de interés" : "Service of interest"}</Label>
          <select name="service" className={inputClass} defaultValue="">
            <option value="" disabled className="bg-gray-900">
              {isEs ? "Selecciona uno..." : "Select one..."}
            </option>
            {SERVICES.map(s => (
              <option key={s.value} value={s.value} className="bg-gray-900">
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Mensaje */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label required>{isEs ? "Mensaje" : "Message"}</Label>
          <span className={`text-[10px] tabular-nums ${msgLen > 480 ? "text-red-400" : "text-gray-600"}`}>
            {msgLen}/500
          </span>
        </div>
        <textarea
          name="message"
          rows={5}
          maxLength={500}
          placeholder={
            isEs
              ? "Cuéntanos sobre tu infraestructura actual y qué necesitas..."
              : "Tell us about your current infrastructure and what you need..."
          }
          className={`${errors.message ? errorInputClass : inputClass} resize-none`}
          onChange={e => {
            setMsgLen(e.target.value.length);
            if (errors.message) setErrors(p => ({ ...p, message: undefined }));
          }}
        />
        <FieldError msg={errors.message} />
      </div>

      {/* Error global */}
      {status === "error" && (
        <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" strokeLinecap="round" />
          </svg>
          {isEs ? "Hubo un error. Intenta de nuevo." : "Something went wrong. Please try again."}
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full flex items-center justify-center gap-2 bg-white hover:bg-gray-100 disabled:opacity-50 text-[#09090b] font-semibold py-3 rounded-lg transition-colors text-sm"
      >
        {status === "loading" ? (
          <>
            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={3} />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            {isEs ? "Enviando..." : "Sending..."}
          </>
        ) : (
          <>
            {isEs ? "Enviar mensaje" : "Send message"}
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8h10M9 4l4 4-4 4" />
            </svg>
          </>
        )}
      </button>

      <p className="text-center text-[11px] text-gray-600">
        {isEs ? "Respondemos en menos de 24 horas." : "We respond in less than 24 hours."}
      </p>
    </form>
  );
}
